import React, { useState } from 'react';
import Section from './Section';
import KpiRow from '../../components/dashboard/KpiRow';
import PipelineBand from '../../components/dashboard/PipelineBand';
import NewRFQModal from '../../components/common/NewRFQModal';
import { Button } from '../../components/ui';

/**
 * /design gallery — dashboard KPI tiles + pipeline band states.
 * Compare with the top half of `design/Dashboard v2.dc.html`.
 * Sample data below mirrors the mock's figures; the live dashboard only ever
 * renders real fetched numbers (unavailable metrics show "—").
 */

const mockTiles = [
  { label: 'Active RFQs', value: '6', sub: 'across 5 stages' },
  { label: 'Awaiting your review', value: '3', tone: 'action' as const, sub: 'oldest waiting 2 days' },
  { label: 'Suppliers engaged', value: '24', sub: '9 responded this week' },
  {
    label: 'Avg. sourcing cycle', value: '11.4', unit: 'days',
    sub: '↓ 18% vs last quarter', subTone: 'success' as const,
  },
];

const thinTiles = [
  { label: 'Active RFQs', value: '1', sub: 'across 1 stage' },
  { label: 'Awaiting your review', value: '0', sub: 'nothing waiting on you' },
  { label: 'Suppliers engaged', value: null, sub: 'no outreach data yet' },
  { label: 'Avg. sourcing cycle', value: null, unit: 'days', sub: 'no completed RFQs yet' },
];

const mockStages = [
  { label: 'Upload BOM', count: 1 },
  { label: 'Criteria', count: 1 },
  { label: 'Discovery', count: 1 },
  { label: 'Outreach', count: 1 },
  { label: 'Quotes & Proposals', count: 2 },
];

const thinStages = [
  { label: 'Upload BOM', count: 1 },
  { label: 'Criteria', count: 0 },
  { label: 'Discovery', count: 0 },
  { label: 'Outreach', count: 0 },
  { label: 'Quotes & Proposals', count: 0 },
];

/** The naming popup demo creates nothing — confirm just closes after a beat. */
const NamingModalDemo: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-s3">
      <p className="text-meta text-ink-muted">New RFQ naming popup (confirm creates; Cancel creates nothing)</p>
      <div>
        <Button onClick={() => setOpen(true)}>Open naming modal</Button>
      </div>
      <NewRFQModal
        open={open}
        onClose={() => setOpen(false)}
        onCreate={async () => {
          await new Promise((resolve) => setTimeout(resolve, 600));
          setOpen(false);
        }}
      />
    </div>
  );
};

const DashboardGallery: React.FC = () => (
  <Section title="Dashboard — KPI row + pipeline band">
    <div className="flex flex-col gap-s6">
      <div className="flex flex-col gap-s3">
        <p className="text-meta text-ink-muted">Populated (mock figures)</p>
        <KpiRow tiles={mockTiles} />
        <PipelineBand stages={mockStages} awardedThisMonth={1} />
      </div>
      <div className="flex flex-col gap-s3">
        <p className="text-meta text-ink-muted">Thin data — honest states, no fake deltas</p>
        <KpiRow tiles={thinTiles} />
        <PipelineBand stages={thinStages} awardedThisMonth={0} />
      </div>
      <NamingModalDemo />
    </div>
  </Section>
);

export default DashboardGallery;
